import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, Plus, Trash2, Pencil, X, Zap, Search, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { api, API } from "@/lib/api";

const STATI = { invitato: "Invitato", confermato: "Confermato", rifiutato: "Rifiutato" };
const BADGE = {
  invitato: "bg-neutral-100 text-neutral-600",
  confermato: "bg-emerald-50 text-emerald-700",
  rifiutato: "bg-rose-50 text-rose-600",
};

const EMPTY = { nome: "", email: "", telefono: "", stato_rsvp: "invitato", num_ospiti_extra: 0, allergie_note: "", moments: [] };

const SORT_VALUE = {
  nome: (g) => g.nome || "",
  contatto: (g) => g.email || g.telefono || "",
  stato_rsvp: (g) => STATI[g.stato_rsvp] || "",
  num_ospiti_extra: (g) => g.num_ospiti_extra || 0,
  numero_tavolo: (g) => g.numero_tavolo ?? -1,
};

export default function GuestsTab({ wedding }) {
  const moments = useMemo(() => wedding?.moments || [], [wedding]);
  const allMomentIds = useMemo(() => moments.map((m) => m.id), [moments]);

  const [guests, setGuests] = useState([]);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: "nome", dir: "asc" });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickText, setQuickText] = useState("");
  const [quickSaving, setQuickSaving] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkSaving, setBulkSaving] = useState(false);

  useEffect(() => { setSelected(new Set()); }, [filter]);

  const toggleSelect = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const quickNames = useMemo(
    () => quickText.split(/[,\n]+/).map((s) => s.trim()).filter(Boolean),
    [quickText],
  );

  const load = useCallback(() => {
    api.get("/owner/guests", { params: filter ? { stato: filter } : {} }).then((r) => setGuests(r.data)).catch(() => {});
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) return toast.error("Nome obbligatorio");
    const payload = { ...form, num_ospiti_extra: Number(form.num_ospiti_extra) || 0, plus_one: Number(form.num_ospiti_extra) > 0 };
    try {
      if (editing === "new") await api.post("/owner/guests", payload);
      else await api.put(`/owner/guests/${editing}`, payload);
      toast.success("Invitato salvato");
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch {
      toast.error("Errore nel salvataggio");
    }
  };

  const toggleMoment = async (guest, momentId) => {
    const current = guest.moments || [];
    const next = current.includes(momentId) ? current.filter((id) => id !== momentId) : [...current, momentId];
    setGuests((gs) => gs.map((g) => (g.id === guest.id ? { ...g, moments: next } : g)));
    try {
      await api.put(`/owner/guests/${guest.id}`, {
        nome: guest.nome,
        email: guest.email,
        telefono: guest.telefono,
        stato_rsvp: guest.stato_rsvp,
        num_ospiti_extra: guest.num_ospiti_extra || 0,
        allergie_note: guest.allergie_note,
        moments: next,
      });
    } catch {
      toast.error("Errore nel salvataggio");
      setGuests((gs) => gs.map((g) => (g.id === guest.id ? { ...g, moments: current } : g)));
    }
  };

  const bulkUpdate = async (buildPayload) => {
    const targets = guests.filter((g) => selected.has(g.id));
    if (!targets.length) return;
    setBulkSaving(true);
    try {
      const results = await Promise.allSettled(
        targets.map((g) =>
          api.put(`/owner/guests/${g.id}`, {
            nome: g.nome,
            email: g.email,
            telefono: g.telefono,
            stato_rsvp: g.stato_rsvp,
            num_ospiti_extra: g.num_ospiti_extra || 0,
            allergie_note: g.allergie_note,
            moments: g.moments || [],
            ...buildPayload(g),
          }),
        ),
      );
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - ok;
      if (ok) toast.success(`${ok} invitat${ok > 1 ? "i aggiornati" : "o aggiornato"}`);
      if (failed) toast.error(`${failed} non aggiornat${failed > 1 ? "i" : "o"}`);
      setSelected(new Set());
      load();
    } finally {
      setBulkSaving(false);
    }
  };

  const bulkSetStatus = (stato_rsvp) => bulkUpdate(() => ({ stato_rsvp }));
  const bulkSetOnlyMoment = (momentId) => bulkUpdate(() => ({ moments: [momentId] }));
  const bulkSetFullDay = () => bulkUpdate(() => ({ moments: allMomentIds }));

  const quickSave = async () => {
    if (!quickNames.length) return toast.error("Inserisci almeno un nome");
    setQuickSaving(true);
    try {
      const results = await Promise.allSettled(
        quickNames.map((nome) => api.post("/owner/guests", { nome, stato_rsvp: "invitato", num_ospiti_extra: 0 })),
      );
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - ok;
      if (ok) toast.success(`${ok} invitat${ok > 1 ? "i aggiunti" : "o aggiunto"}`);
      if (failed) toast.error(`${failed} non salvat${failed > 1 ? "i" : "o"}`);
      setQuickText("");
      setQuickOpen(false);
      load();
    } finally {
      setQuickSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/owner/guests/${id}`);
    toast.success("Invitato eliminato");
    load();
  };

  const exportCsv = async () => {
    const r = await api.get("/owner/guests/export", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "invitati.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const stats = {
    confermato: guests.filter((g) => g.stato_rsvp === "confermato").length,
    totOspiti: guests.filter((g) => g.stato_rsvp === "confermato").reduce((s, g) => s + 1 + (g.num_ospiti_extra || 0), 0),
  };

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const visibleGuests = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? guests.filter((g) => [g.nome, g.email, g.telefono, g.allergie_note].some((v) => v?.toLowerCase().includes(q)))
      : guests;
    const getValue = SORT_VALUE[sort.key] || SORT_VALUE.nome;
    const sorted = [...filtered].sort((a, b) => {
      const va = getValue(a), vb = getValue(b);
      const cmp = typeof va === "number" ? va - vb : String(va).localeCompare(String(vb), "it", { sensitivity: "base" });
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [guests, search, sort]);

  // Selecting "all" only selects what's currently visible (filtered/searched),
  // matching what the checkbox in the header visually sits above.
  const toggleSelectAll = () => {
    setSelected((s) => (s.size === visibleGuests.length ? new Set() : new Set(visibleGuests.map((g) => g.id))));
  };

  function SortHeader({ label, sortKey, className = "" }) {
    const active = sort.key === sortKey;
    const Icon = active ? (sort.dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
      <th className={`sticky top-0 z-10 bg-white px-4 py-3 font-medium border-b border-[#eee8dd] ${className}`}>
        <button type="button" onClick={() => toggleSort(sortKey)} className={`flex items-center gap-1 ${active ? "text-neutral-700" : "text-neutral-400 hover:text-neutral-600"}`} data-testid={`guests-sort-${sortKey}`}>
          {label} <Icon size={12} />
        </button>
      </th>
    );
  }

  return (
    <div data-testid="guests-tab">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            className="input-wed !py-2 pl-9 text-sm w-56"
            placeholder="Cerca nome, email, telefono…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="guests-search-input"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {["", "invitato", "confermato", "rifiutato"].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-4 py-1.5 rounded-full text-sm border transition-colors duration-200 ${filter === s ? "text-white" : "bg-white text-neutral-600"}`}
              style={filter === s ? { background: "var(--wed-primary)", borderColor: "var(--wed-primary)" } : { borderColor: "#e3ddd2" }}
              data-testid={`guests-filter-${s || "all"}`}
            >
              {s ? STATI[s] : "Tutti"}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button className="btn-outline-wed !py-2 !px-4 text-sm flex items-center gap-2" onClick={exportCsv} data-testid="guests-export-button">
            <Download size={15} /> Export CSV
          </button>
          <button className="btn-outline-wed !py-2 !px-4 text-sm flex items-center gap-2" onClick={() => { setQuickOpen(true); setEditing(null); }} data-testid="guests-quick-add-button">
            <Zap size={15} /> Aggiunta rapida
          </button>
          <button className="btn-wed !py-2 !px-4 text-sm flex items-center gap-2" onClick={() => { setEditing("new"); setForm({ ...EMPTY, moments: allMomentIds }); setQuickOpen(false); }} data-testid="guests-add-button">
            <Plus size={15} /> Aggiungi
          </button>
        </div>
      </div>

      <p className="text-sm text-neutral-500 mb-4" data-testid="guests-stats">
        {visibleGuests.length}{search ? ` di ${guests.length}` : ""} invitati{filter ? ` (${STATI[filter]?.toLowerCase()})` : ""} · {stats.confermato} confermati · {stats.totOspiti} presenze totali
      </p>

      {selected.size > 0 && (
        <div className="bg-white border rounded-2xl p-4 mb-4 flex flex-wrap items-center gap-3 shadow-md" style={{ borderColor: "var(--wed-primary)" }} data-testid="guests-bulk-bar">
          <span className="text-sm font-medium whitespace-nowrap">{selected.size} selezionat{selected.size === 1 ? "o" : "i"}</span>
          <div className="h-5 w-px bg-neutral-200" />
          <div className="flex gap-1 flex-wrap">
            {Object.entries(STATI).map(([k, v]) => (
              <button
                key={k}
                type="button"
                disabled={bulkSaving}
                onClick={() => bulkSetStatus(k)}
                className="px-3 py-1.5 rounded-full text-xs border bg-white text-neutral-600 hover:bg-neutral-50 transition-colors duration-150 disabled:opacity-50"
                style={{ borderColor: "#e3ddd2" }}
                data-testid={`guests-bulk-status-${k}`}
              >
                {v}
              </button>
            ))}
          </div>
          {moments.length > 0 && (
            <>
              <div className="h-5 w-px bg-neutral-200" />
              <div className="flex gap-1 flex-wrap items-center">
                <span className="text-xs text-neutral-400">Imposta presenza:</span>
                {moments.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    disabled={bulkSaving}
                    onClick={() => bulkSetOnlyMoment(m.id)}
                    className="px-3 py-1.5 rounded-full text-xs border bg-white text-neutral-600 hover:bg-neutral-50 transition-colors duration-150 disabled:opacity-50"
                    style={{ borderColor: "#e3ddd2" }}
                    data-testid={`guests-bulk-moment-${m.id}`}
                  >
                    Solo {m.label}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={bulkSaving}
                  onClick={bulkSetFullDay}
                  className="px-3 py-1.5 rounded-full text-xs border bg-white text-neutral-600 hover:bg-neutral-50 transition-colors duration-150 disabled:opacity-50"
                  style={{ borderColor: "#e3ddd2" }}
                  data-testid="guests-bulk-moment-fullday"
                >
                  Giornata intera
                </button>
              </div>
            </>
          )}
          <button type="button" onClick={() => setSelected(new Set())} className="ml-auto text-neutral-400 hover:text-neutral-700 text-sm" data-testid="guests-bulk-clear">
            Deseleziona
          </button>
        </div>
      )}

      {quickOpen && (
        <div className="bg-white border rounded-2xl p-5 mb-6 space-y-3" style={{ borderColor: "#e3ddd2" }} data-testid="guests-quick-add-panel">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="font-wed text-lg">Aggiunta rapida</h3>
              <p className="text-xs text-neutral-400 mt-0.5">Un nome per riga, oppure separati da virgola. Verranno aggiunti come "Invitato".</p>
            </div>
            <button type="button" onClick={() => setQuickOpen(false)} className="text-neutral-400 hover:text-neutral-700" data-testid="guests-quick-add-close"><X size={18} /></button>
          </div>
          <textarea
            className="input-wed w-full min-h-[120px]"
            placeholder={"Mario Rossi\nAnna Bianchi\nLuca Verdi, Sara Neri"}
            value={quickText}
            onChange={(e) => setQuickText(e.target.value)}
            data-testid="guests-quick-add-textarea"
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-neutral-400" data-testid="guests-quick-add-count">
              {quickNames.length} nom{quickNames.length === 1 ? "e" : "i"} pront{quickNames.length === 1 ? "o" : "i"}
            </span>
            <button
              className="btn-wed !py-2 !px-4 text-sm"
              onClick={quickSave}
              disabled={quickSaving || !quickNames.length}
              data-testid="guests-quick-add-save"
            >
              {quickSaving ? "Salvataggio…" : `Aggiungi ${quickNames.length || ""}`.trim()}
            </button>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-[120] bg-black/50 flex items-center justify-center p-4" onClick={() => setEditing(null)} data-testid="guest-form-modal">
          <form
            onSubmit={save}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-[#faf9f6] rounded-3xl p-7 space-y-4 max-h-[92vh] overflow-y-auto"
            data-testid="guest-form"
          >
            <div className="flex justify-between items-center">
              <h3 className="font-wed text-2xl">{editing === "new" ? "Nuovo invitato" : "Modifica invitato"}</h3>
              <button type="button" onClick={() => setEditing(null)} className="text-neutral-400 hover:text-neutral-700" data-testid="guest-form-close"><X size={20} /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <input className="input-wed" placeholder="Nome *" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="guest-form-name" />
              <input className="input-wed" placeholder="Email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="guest-form-email" />
              <input className="input-wed" placeholder="Telefono" value={form.telefono || ""} onChange={(e) => setForm({ ...form, telefono: e.target.value })} data-testid="guest-form-phone" />
              <select className="input-wed" value={form.stato_rsvp} onChange={(e) => setForm({ ...form, stato_rsvp: e.target.value })} data-testid="guest-form-status">
                {Object.entries(STATI).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <div>
                <label className="label-wed">Ospiti extra (accompagnatori)</label>
                <input className="input-wed w-full" type="number" min={0} value={form.num_ospiti_extra} onChange={(e) => setForm({ ...form, num_ospiti_extra: e.target.value })} data-testid="guest-form-extra" />
              </div>
              <input className="input-wed" placeholder="Allergie / note" value={form.allergie_note || ""} onChange={(e) => setForm({ ...form, allergie_note: e.target.value })} data-testid="guest-form-allergies" />
            </div>
            {moments.length > 0 && (
              <div>
                <label className="label-wed">Presenza</label>
                <div className="flex flex-wrap gap-2">
                  {moments.map((m) => {
                    const checked = form.moments.includes(m.id);
                    return (
                      <label
                        key={m.id}
                        className={`px-3 py-1.5 rounded-full text-sm border cursor-pointer transition-colors duration-150 ${checked ? "text-white" : "bg-white text-neutral-600"}`}
                        style={checked ? { background: "var(--wed-primary)", borderColor: "var(--wed-primary)" } : { borderColor: "#e3ddd2" }}
                        data-testid={`guest-form-moment-${m.id}`}
                      >
                        <input
                          type="checkbox"
                          className="hidden"
                          checked={checked}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              moments: e.target.checked ? [...form.moments, m.id] : form.moments.filter((id) => id !== m.id),
                            })
                          }
                        />
                        {m.label}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            <button type="submit" className="btn-wed w-full" data-testid="guest-form-save">Salva</button>
          </form>
        </div>
      )}

      <div className="bg-white border rounded-2xl overflow-auto max-h-[65vh]" style={{ borderColor: "#e3ddd2" }}>
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="text-left text-neutral-400">
              <th className="sticky top-0 z-10 bg-white px-4 py-3 border-b border-[#eee8dd] w-10">
                <input
                  type="checkbox"
                  checked={visibleGuests.length > 0 && selected.size === visibleGuests.length}
                  onChange={toggleSelectAll}
                  data-testid="guests-select-all"
                />
              </th>
              <SortHeader label="Nome" sortKey="nome" />
              <SortHeader label="Contatto" sortKey="contatto" />
              <SortHeader label="RSVP" sortKey="stato_rsvp" />
              <SortHeader label="Extra" sortKey="num_ospiti_extra" />
              <th className="sticky top-0 z-10 bg-white px-4 py-3 font-medium border-b border-[#eee8dd]">Note</th>
              <SortHeader label="Tavolo" sortKey="numero_tavolo" />
              {moments.length > 0 && (
                <th className="sticky top-0 z-10 bg-white px-4 py-3 font-medium border-b border-[#eee8dd]">Presenza</th>
              )}
              <th className="sticky top-0 z-10 bg-white px-4 py-3 border-b border-[#eee8dd]"></th>
            </tr>
          </thead>
          <tbody data-testid="guests-table-body">
            {visibleGuests.map((g) => (
              <tr key={g.id} className={`border-b last:border-0 hover:bg-neutral-50 transition-colors duration-150 ${selected.has(g.id) ? "bg-[#faf9f6]" : ""}`} style={{ borderColor: "#f4f0e8" }} data-testid={`guest-row-${g.id}`}>
                <td className="px-4 py-3">
                  <input type="checkbox" checked={selected.has(g.id)} onChange={() => toggleSelect(g.id)} data-testid={`guest-select-${g.id}`} />
                </td>
                <td className="px-4 py-3 font-medium">{g.nome}</td>
                <td className="px-4 py-3 text-neutral-500">{g.email || g.telefono || "—"}</td>
                <td className="px-4 py-3"><span className={`px-2.5 py-1 rounded-full text-xs ${BADGE[g.stato_rsvp]}`}>{STATI[g.stato_rsvp]}</span></td>
                <td className="px-4 py-3">{g.num_ospiti_extra || 0}</td>
                <td className="px-4 py-3 text-neutral-500 max-w-[180px] truncate">{g.allergie_note || "—"}</td>
                <td className="px-4 py-3">{g.numero_tavolo ?? "—"}</td>
                {moments.length > 0 && (
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {moments.map((m) => {
                        const checked = (g.moments || []).includes(m.id);
                        return (
                          <button
                            key={m.id}
                            type="button"
                            onClick={() => toggleMoment(g, m.id)}
                            className={`px-2 py-0.5 rounded-full text-xs border whitespace-nowrap transition-colors duration-150 ${checked ? "text-white" : "bg-white text-neutral-400 hover:text-neutral-600"}`}
                            style={checked ? { background: "var(--wed-primary)", borderColor: "var(--wed-primary)" } : { borderColor: "#e3ddd2" }}
                            data-testid={`guest-moment-toggle-${g.id}-${m.id}`}
                          >
                            {m.label}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                )}
                <td className="px-4 py-3">
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => { setEditing(g.id); setForm({ nome: g.nome, email: g.email, telefono: g.telefono, stato_rsvp: g.stato_rsvp, num_ospiti_extra: g.num_ospiti_extra || 0, allergie_note: g.allergie_note, moments: g.moments || [] }); setQuickOpen(false); }} className="p-1.5 text-neutral-400 hover:text-neutral-700 transition-colors duration-150" data-testid={`guest-edit-${g.id}`}><Pencil size={15} /></button>
                    <button onClick={() => remove(g.id)} className="p-1.5 text-neutral-400 hover:text-rose-500 transition-colors duration-150" data-testid={`guest-delete-${g.id}`}><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {visibleGuests.length === 0 && (
              <tr><td colSpan={moments.length > 0 ? 9 : 8} className="px-4 py-8 text-center text-neutral-400">{search ? "Nessun invitato trovato" : "Nessun invitato"}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
